import React, { useState } from 'react';
import { View, Text, TouchableOpacity, Platform, ActivityIndicator } from 'react-native';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useUser } from './LoginPersistence';
import { TESTER_ACCOUNTS, isTesterUser } from '../utils/testerAccounts';

const ACCENT = '#9b59b6';

/**
 * One button per QA tester account. The switchToTesterAccount function checks the caller is an
 * admin or a tester and only ever signs in as a tester account, so no password is typed.
 */
export function TesterSwitchButtons({ currentEmail, isDarkMode = true, compact = false }) {
  const [busyRole, setBusyRole] = useState(null);
  const [error, setError] = useState('');
  const current = String(currentEmail || getAuth().currentUser?.email || '').toLowerCase();

  const switchTo = async (role) => {
    setBusyRole(role);
    setError('');
    try {
      const call = httpsCallable(getFunctions(), 'switchToTesterAccount');
      const { data } = await call({ role });
      await signInWithCustomToken(getAuth(), data.token);
      // Start clean as the new account: screens hold per-user state. Only tester-admin may stay on
      // an /admin page; anyone else would bounce to Home with "Access denied", so go there directly.
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        if (role !== 'admin' && /^\/admin(\/|$)/.test(window.location.pathname)) window.location.assign('/home');
        else window.location.reload();
      }
    } catch (e) {
      setError(e?.message || 'Could not switch accounts.');
      setBusyRole(null);
    }
  };

  // Web only: the page reload is what clears the previous account's screen state.
  if (Platform.OS !== 'web') return null;
  const text = isDarkMode ? '#ffffff' : '#1a1a2e';
  const sub = isDarkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)';

  return (
    <View style={{ gap: 8 }}>
      {TESTER_ACCOUNTS.map((a) => {
        const isCurrent = a.email === current;
        return (
          <TouchableOpacity
            key={a.role}
            accessibilityRole="button"
            accessibilityLabel={`Switch to ${a.label}`}
            disabled={isCurrent || !!busyRole}
            onPress={() => switchTo(a.role)}
            style={{
              borderWidth: 1.5,
              borderColor: ACCENT,
              backgroundColor: isCurrent ? ACCENT : 'transparent',
              borderRadius: 10,
              paddingVertical: compact ? 8 : 10,
              paddingHorizontal: 12,
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              opacity: busyRole && busyRole !== a.role ? 0.5 : 1,
            }}
          >
            <View style={{ flex: 1 }}>
              <Text style={{ color: isCurrent ? '#fff' : text, fontWeight: '700', fontSize: 14 }}>
                {a.label}{isCurrent ? ' (you)' : ''}
              </Text>
              {!compact && (
                <Text style={{ color: isCurrent ? 'rgba(255,255,255,0.85)' : sub, fontSize: 12, marginTop: 2 }} numberOfLines={2}>
                  {a.description}
                </Text>
              )}
            </View>
            {busyRole === a.role && <ActivityIndicator size="small" color={isCurrent ? '#fff' : ACCENT} />}
          </TouchableOpacity>
        );
      })}
      {!!error && <Text style={{ color: '#e74c3c', fontSize: 12 }}>{error}</Text>}
    </View>
  );
}

/** Floating switcher, shown only while signed in as a tester account (web). */
export default function TesterSwitcher() {
  const { user } = useUser();
  const [open, setOpen] = useState(false);
  if (Platform.OS !== 'web' || !isTesterUser(user)) return null;
  const me = TESTER_ACCOUNTS.find((a) => a.email === String(user.email || '').toLowerCase());

  return (
    <View style={{ position: 'fixed', left: 12, bottom: 96, zIndex: 9999, maxWidth: 280 }} pointerEvents="box-none">
      {open && (
        <View
          style={{
            backgroundColor: '#1a1a2e',
            borderRadius: 14,
            padding: 12,
            marginBottom: 8,
            borderWidth: 1,
            borderColor: 'rgba(155,89,182,0.6)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
          }}
        >
          <Text style={{ color: '#fff', fontWeight: '800', fontSize: 13, marginBottom: 8 }}>Switch tester account</Text>
          <TesterSwitchButtons currentEmail={user.email} compact />
        </View>
      )}
      <TouchableOpacity
        accessibilityRole="button"
        accessibilityLabel={open ? 'Close tester switcher' : 'Open tester switcher'}
        onPress={() => setOpen((v) => !v)}
        style={{ alignSelf: 'flex-start', backgroundColor: ACCENT, borderRadius: 999, paddingVertical: 6, paddingHorizontal: 12 }}
      >
        <Text style={{ color: '#fff', fontWeight: '700', fontSize: 12 }}>{me ? me.label : 'Tester'} ▾</Text>
      </TouchableOpacity>
    </View>
  );
}
